import { createPolicyItemConfigurationResolver } from '../../../model/policy-item-configuration.ts'
import type { PolicyItems } from '../../../model/policy-item.ts'
import { nlAovqlInflationIndexationRates } from '../nl-aovql-cover-configuration.ts'
import type { NlAovqlPolicyItem, NlAovqlPolicyItemsState } from '../nl-aovql-insurance.ts'

type NlAovqlPolicyItemResolverRuntimeState = {
  subscriptionActionType?: 'renewal' | 'quote' | 'endorsement' | 'display'
}

function isNlAovqlInflationIndexationLocked(state: NlAovqlPolicyItemResolverRuntimeState): boolean {
  if (state.subscriptionActionType === undefined) return false

  switch (state.subscriptionActionType) {
    // The steps count from the first contract year, so a change on a running contract would reprice every past
    // step at once inducing a huge jump in the premium
    case 'endorsement':
    case 'renewal':
      return true
    case 'quote':
    case 'display':
      return false
  }
}

export const nlAovqlPolicyItemsV1_0_0 = {
  mentalDisorders: {
    name: 'mentalDisorders',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: { type: 'none' },
    possibleDeductibles: { type: 'not-applicable' },
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: () => ({
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: true,
      }),
      backofficeConfiguration: () => ({
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: true,
      }),
    }),
    additionalInfo: null,
    dependsOn: [],
  },
  inflationIndexation: {
    name: 'inflationIndexation',
    type: 'service',
    optionality: ['optional'],
    possibleLimitOfIndemnities: {
      type: 'percentages',
      percentages: [...nlAovqlInflationIndexationRates],
      baseText: '',
    },
    possibleDeductibles: { type: 'not-applicable' },
    // The funnel offers the default rate only, the back office may pick another one from the list.
    editConfiguration: createPolicyItemConfigurationResolver({
      selfOnboardingConfiguration: ({ state }: { state: NlAovqlPolicyItemResolverRuntimeState }) => ({
        isHidden: false,
        canBeUpdated: false,
        canBeSelected: !isNlAovqlInflationIndexationLocked(state),
      }),
      backofficeConfiguration: ({ state }: { state: NlAovqlPolicyItemResolverRuntimeState }) => {
        const isLocked = isNlAovqlInflationIndexationLocked(state)
        return {
          isHidden: false,
          canBeUpdated: !isLocked,
          canBeSelected: !isLocked,
          limitOfIndemnityControl: 'dropdown',
        }
      },
    }),
    additionalInfo: null,
    dependsOn: [],
  },
} as const satisfies PolicyItems<NlAovqlPolicyItem, NlAovqlPolicyItemsState>
